import React, { useMemo, useState } from "react";
import MappingTopBox from "./CreatorTools/MappingTool/components/MappingTopBox";
import MatchCriteriaSelector from "./CreatorTools/MappingTool/components/MatchCriteriaSelector";
import AddEstimateHeaderModal from "./CreatorTools/MappingTool/components/AddEstimateHeaderModal";
import { useFirestoreCollectionReadWrite } from "../hooks/useFirestoreCollectionReadWrite";
import { EstimateExportHeaderConfig, EstimateMatchCriteria } from "./types";
import { EstimateType } from "./CreatorTools/MappingTool/utils/EstimateType";
import { v4 as uuidv4 } from "uuid";

interface ConfirmDropData {
	draggedIndex: number;
	targetIndex: number;
	draggedHeader: string;
	targetHeader: string;
}

const EstimateExportHeadersPage: React.FC = () => {
	const { objects: rawConfigs, isLoading, isUpdating, saveObject } = useFirestoreCollectionReadWrite<EstimateExportHeaderConfig>(
		"estimate_header_config",
		{ idField: "uniqueId" },
	);
	const configs = useMemo(() => rawConfigs.map((c) => ({ ...c, headers: c.headers ?? [] })), [rawConfigs]);

	const saveHeaders = async (definitionId: string | undefined, matchCriteria: EstimateMatchCriteria, headers: string[]) => {
		const uniqueId = definitionId ?? uuidv4();
		const matchCriteriaWithDefinedFields = Object.fromEntries(
			Object.entries(matchCriteria).filter(([_, value]) => value !== undefined),
		) as EstimateMatchCriteria;
		await saveObject(uniqueId, { uniqueId, matchCriteria: matchCriteriaWithDefinedFields, headers });
	};

	const deleteHeader = async (definitionId: string, headerIndex: number) => {
		const config = configs.find((c) => c.uniqueId === definitionId);
		if (!config) return;
		const updatedHeaders = config.headers.filter((_, i) => i !== headerIndex);
		await saveObject(definitionId, { ...config, headers: updatedHeaders });
	};

	const [matchCriteria, setMatchCriteria] = useState<EstimateMatchCriteria>({
		estimateType: EstimateType.MITIGATION,
	});
	const [showAddModal, setShowAddModal] = useState(false);
	const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
	const [confirmDropData, setConfirmDropData] = useState<ConfirmDropData | null>(null);

	const selectedConfig = useMemo(() => {
		if (isLoading) return undefined;
		return configs.find(
			(c) =>
				c.matchCriteria.organizationId === matchCriteria.organizationId &&
				c.matchCriteria.estimateType === matchCriteria.estimateType &&
				c.matchCriteria.carrierId === matchCriteria.carrierId &&
				c.matchCriteria.tpaId === matchCriteria.tpaId,
		);
	}, [configs, isLoading, matchCriteria]);

	const headers = selectedConfig?.headers ?? [];

	const handleAddHeader = async (header: string) => {
		await saveHeaders(selectedConfig?.uniqueId, matchCriteria, [...headers, header]);
		setShowAddModal(false);
	};

	const handleDeleteHeader = async (index: number) => {
		if (!selectedConfig) return;
		await deleteHeader(selectedConfig.uniqueId, index);
	};

	// Drag and drop handlers
	const handleDragStart = (e: React.DragEvent, index: number) => {
		setDraggedIndex(index);
		e.dataTransfer.effectAllowed = "move";
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	};

	const handleDrop = (e: React.DragEvent, targetIndex: number) => {
		e.preventDefault();
		if (draggedIndex === null || draggedIndex === targetIndex) return;

		setConfirmDropData({
			draggedIndex,
			targetIndex,
			draggedHeader: headers[draggedIndex],
			targetHeader: headers[targetIndex],
		});
	};

	const handleDragEnd = () => {
		setDraggedIndex(null);
	};

	const confirmDrop = async () => {
		if (!confirmDropData) return;

		const { draggedIndex: from, targetIndex: to } = confirmDropData;
		const newHeaders = [...headers];
		const [removed] = newHeaders.splice(from, 1);
		newHeaders.splice(to, 0, removed);

		await saveHeaders(selectedConfig?.uniqueId, matchCriteria, newHeaders);
		setConfirmDropData(null);
		setDraggedIndex(null);
	};

	const cancelDrop = () => {
		setConfirmDropData(null);
		setDraggedIndex(null);
	};

	return (
		<div className="min-h-screen bg-gray-50">
			<MappingTopBox />
			<div className="mx-8 mt-4">
				<div className="mb-4">
					<h1 className="text-3xl font-bold text-gray-800 mb-2">Estimate Export Headers</h1>
					<p className="text-gray-600">Configure export headers for specific customers.</p>
					<p className="text-gray-600">These headers will appear at the bottom of each room in the Xactimate export.</p>
				</div>
				{!matchCriteria.organizationId && (
					<div className="mb-4 flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
						<svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
						</svg>
						<span>
							<strong>Default organization selected.</strong> Headers configured here would apply to all customers by default, which is almost never the right thing to do for estimate export headers. Select a specific organization instead.
						</span>
					</div>
				)}
				<div className="flex items-end gap-4 mb-6">
					<MatchCriteriaSelector matchCriteria={matchCriteria} setMatchCriteria={setMatchCriteria} includeTestOrgs />
					<div className="ml-auto">
						<button
							onClick={() => setShowAddModal(true)}
							className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
						>
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
							</svg>
							Add Estimate Header
						</button>
					</div>
				</div>

				{isLoading ? (
					<div className="text-gray-600">Loading headers...</div>
				) : headers.length === 0 ? (
					<p className="text-gray-500">No headers configured for this criteria.</p>
				) : (
					<div className="bg-white rounded-lg shadow">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Index</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Header</th>
									<th className="px-6 py-3 w-16" />
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{headers.map((header, index) => (
									<tr
										key={index}
										draggable
										onDragStart={(e) => handleDragStart(e, index)}
										onDragOver={handleDragOver}
										onDrop={(e) => handleDrop(e, index)}
										onDragEnd={handleDragEnd}
										className={`cursor-move hover:bg-gray-50 transition-colors ${draggedIndex === index ? "opacity-50" : ""}`}
									>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index}</td>
										<td className="px-6 py-4 text-sm text-gray-900">{header}</td>
										<td className="px-6 py-4 text-right">
											<button
												onClick={() => handleDeleteHeader(index)}
												disabled={isUpdating}
												className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
												title="Remove header"
											>
												✕
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			<AddEstimateHeaderModal
				isOpen={showAddModal}
				onClose={() => setShowAddModal(false)}
				onAdd={handleAddHeader}
				isUpdating={isUpdating}
			/>

			{confirmDropData && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm reorder</h3>
						<p className="text-gray-700 mb-6">
							Move <span className="font-semibold">"{confirmDropData.draggedHeader}"</span> to index{" "}
							<span className="font-semibold">{confirmDropData.targetIndex}</span>, placing it{" "}
							{confirmDropData.draggedIndex < confirmDropData.targetIndex ? "after" : "before"}{" "}
							<span className="font-semibold">"{confirmDropData.targetHeader}"</span>?
						</p>
						<div className="flex justify-end gap-3">
							<button
								onClick={cancelDrop}
								className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
							>
								Cancel
							</button>
							<button
								onClick={confirmDrop}
								disabled={isUpdating}
								className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
							>
								Confirm
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default EstimateExportHeadersPage;
